import { Box, Button, ListItemText, Menu, MenuItem, Tooltip, useTheme } from "@mui/material";
import { ChevronRight } from "@procore/core-icons";
import { Fragment, ReactNode, useState } from "react";

import { BreadcrumbNodeSpec, Node } from "./types";

export function Breadcrumbs<T>({
    breadcrumbs,
    onClick,
}: {
    breadcrumbs: BreadcrumbNodeSpec<T>[] | null;
    onClick: (node: Node<T>) => void;
}) {
    const theme = useTheme();
    if (!breadcrumbs || breadcrumbs.length <= 1) {
        return null;
    }

    return (
        <Box
            boxShadow={theme.customShadows.widgetHeader}
            onClick={(e) => e.stopPropagation()}
            sx={{
                position: "absolute",
                left: 0,
                top: 0,
                width: "100%",
                zIndex: 1,
                background: "white",
                display: "flex",
                alignItems: "center",
                flexWrap: "nowrap",
                overflow: "hidden",
            }}
        >
            {breadcrumbs.map((spec, i) => {
                const isLast = i === breadcrumbs.length - 1;
                let content: ReactNode;
                if (Array.isArray(spec)) {
                    content = <NodeGroupMenu nodes={spec} onClick={onClick} />;
                } else {
                    const { node, label } = spec;
                    content = (
                        <Tooltip title={label !== node.name ? node.name : undefined}>
                            <Button
                                color="grey"
                                onClick={() => onClick(node)}
                                size="small"
                                sx={{
                                    minWidth: "unset",
                                    borderRadius: 0,
                                    ...(isLast && {
                                        flexShrink: 1,
                                        minWidth: 0,
                                        overflow: "hidden",
                                        fontWeight: "bold",
                                    }),
                                }}
                            >
                                <Box
                                    component="span"
                                    sx={
                                        isLast
                                            ? {
                                                  overflow: "hidden",
                                                  textOverflow: "ellipsis",
                                                  whiteSpace: "nowrap",
                                                  display: "block",
                                              }
                                            : undefined
                                    }
                                >
                                    {label}
                                </Box>
                            </Button>
                        </Tooltip>
                    );
                }

                return (
                    <Fragment key={i}>
                        {i > 0 && (
                            <span
                                aria-hidden="true"
                                role="presentation"
                                style={{
                                    flexShrink: 0,
                                    display: "inline-flex",
                                    alignItems: "center",
                                    padding: "0 2px",
                                }}
                            >
                                <ChevronRight size="sm" />
                            </span>
                        )}
                        <span style={isLast ? { overflow: "hidden", flexShrink: 1, minWidth: 0 } : { flexShrink: 0 }}>
                            {content}
                        </span>
                    </Fragment>
                );
            })}
        </Box>
    );
}

function NodeGroupMenu<T>({ nodes, onClick }: { nodes: Node<T>[]; onClick: (node: Node<T>) => void }) {
    const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);

    const openMenu = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        setMenuAnchor(e.currentTarget);
    };

    const closeMenu = () => {
        setMenuAnchor(null);
    };

    return (
        <>
            <Button color="grey" size="small" sx={{ minWidth: "unset" }} onClick={openMenu}>
                ...
            </Button>

            <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={closeMenu}>
                {nodes.map((node) => (
                    <MenuItem
                        key={node.path}
                        onClick={() => {
                            closeMenu();
                            onClick(node);
                        }}
                    >
                        <ListItemText>{node.name}</ListItemText>
                    </MenuItem>
                ))}
            </Menu>
        </>
    );
}
